import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiArrowLeft, FiCheck, FiSearch, FiUser, FiX } from 'react-icons/fi';
import { FaCalculator, FaCreditCard, FaBarcode } from 'react-icons/fa';
import styled, { keyframes } from 'styled-components';
import api from '../../../../../../services/api';
import { useToast } from '../../../../../../hooks/Toast';
import { useModal } from '../../../../../../hooks/Modal';
import { useAuth } from '../../../../../../hooks/Auth';
import { useSearch } from '../../../../../../hooks/Search';
import { useConfiguration } from 'src/hooks/Configuration';

// =========================================================
// Tipos
// =========================================================
interface Client {
  _id: string;
  name: string;
  email?: string;
  invoice_count?: number;
  waiting_count?: number;
}

interface Invoice {
  _id: string;
  installment_number: number;
  payment_date: string;
  installment_value: number;
  price?: number;
  juros: number;
  multa: number;
  total: number;
  payment_status_id: string;       // 'paid' | 'waiting' | 'cancelled' | ...
  payment_method_id: string;       // 'boleto_recurring' | 'credit_card_recurring' | ...
  invoice_hash: string;
  approved_at?: string;
  product_id_response?: { title?: string };
}

interface RecalcRow {
  invoice: Invoice;
  current:  { juros: number; multa: number; installment_value: number };
  proposed: { juros: number; multa: number; installment_value: number };
  changed: boolean;
}

interface MethodRow {
  invoice: Invoice;
  current_method: string;
  proposed_method: string;
  changed: boolean;
}

type Step = 'select-client' | 'view-invoices' | 'preview-recalc' | 'preview-method';
type PaymentMethod = 'boleto_recurring' | 'credit_card_recurring';

// =========================================================
// Helpers
// =========================================================
const formatBRL = (cents: number) =>
  (Number(cents || 0) / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });

const formatDate = (d: string) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const methodLabel = (m: string) =>
  m === 'boleto_recurring' ? 'Boleto'
  : m === 'credit_card_recurring' ? 'Cartão'
  : m || '—';

const statusLabel = (s: string) => ({
  paid: 'Pago', waiting: 'Aguardando', cancelled: 'Cancelado',
  verificating: 'Verificando'
} as Record<string, string>)[s] || s;

/**
 * Recálculo padrão (frontend, provisório):
 *   - Apenas faturas com status === 'waiting' e vencidas
 *   - Base de cálculo: price (se presente) || installment_value original
 *   - multa: 2% da base (uma única vez)
 *   - juros: 1% ao mês a partir do vencimento (pro rata: 1/30 ao dia)
 *   - installment_value novo = base + juros + multa
 * Substituir por endpoint canônico quando a regra do planilha estiver no backend.
 */
const computeProposedCharges = (inv: Invoice) => {
  // base de cálculo: price preservado, senão o installment_value atual
  const base = Number(inv.price ?? inv.installment_value ?? 0);

  if (inv.payment_status_id !== 'waiting') {
    return {
      juros: inv.juros || 0,
      multa: inv.multa || 0,
      installment_value: inv.installment_value || 0
    };
  }
  const due = inv.payment_date ? new Date(inv.payment_date) : null;
  const today = new Date();
  const daysOverdue = due
    ? Math.max(0, Math.floor((today.getTime() - due.getTime()) / 86400000))
    : 0;

  if (daysOverdue <= 0) {
    return { juros: 0, multa: 0, installment_value: base };
  }
  const multa = Math.round(base * 0.02);
  const juros = Math.round(base * 0.01 * (daysOverdue / 30));
  const installment_value = base + multa + juros;
  return { juros, multa, installment_value };
};

// =========================================================
// Styled
// =========================================================
const spin = keyframes`from { transform: rotate(0); } to { transform: rotate(360deg); }`;

const Container = styled.div`
  background: #fff;
  border-radius: 10px;
  min-width: 360px;
  max-width: 880px;
  width: 100%;
  color: #1f2937;
  font-family: 'Inter', -apple-system, sans-serif;
  display: flex;
  flex-direction: column;
  max-height: 80vh;
`;
const Header = styled.div`
  padding: 18px 22px;
  border-bottom: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  gap: 12px;
`;
const HeaderBack = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  &:hover { color: var(--adminColor); }
`;
const HeaderTitle = styled.div`
  flex: 1;
  h3 { font-size: 16px; font-weight: 800; color: #111827; }
  p { font-size: 12px; color: #6b7280; margin-top: 2px; }
`;
const Body = styled.div`
  padding: 18px 22px;
  overflow-y: auto;
  flex: 1;
`;
const Footer = styled.div`
  padding: 14px 22px;
  border-top: 1px solid #f3f4f6;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
`;
const SearchBox = styled.div`
  position: relative;
  margin-bottom: 14px;
  > svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #9ca3af; }
  > input {
    width: 100%; height: 40px; padding: 0 12px 0 36px;
    border: 1.5px solid #e5e7eb; border-radius: 8px; font-size: 13px;
    font-family: inherit;
    &:focus { outline: none; border-color: var(--adminColor); }
  }
`;
const ClientList = styled.div`
  display: flex; flex-direction: column; gap: 6px; max-height: 380px; overflow-y: auto;
`;
const ClientCard = styled.button`
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px;
  border: 1.5px solid #e5e7eb; border-radius: 8px;
  background: #fff; text-align: left; cursor: pointer; transition: all .12s;
  &:hover { border-color: var(--adminColor); background: rgba(111,33,118,.04); }
  .avatar {
    width: 32px; height: 32px; border-radius: 999px;
    background: rgba(111,33,118,.1); color: var(--adminColor);
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .info { flex: 1; }
  .info .name { font-weight: 600; font-size: 13px; color: #111827; }
  .info .meta { font-size: 11px; color: #6b7280; margin-top: 1px; }
  .badge {
    font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 999px;
    background: rgba(111,33,118,.1); color: var(--adminColor);
  }
`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 12px;
  th {
    text-align: left; padding: 10px 8px; font-size: 11px; font-weight: 600;
    color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em;
    border-bottom: 1px solid #e5e7eb; background: #fafafa;
  }
  td {
    padding: 10px 8px; border-bottom: 1px solid #f3f4f6; color: #1f2937;
    vertical-align: middle;
  }
  tr.changed td { background: rgba(111,33,118,0.05); }
  tr.unchanged td { color: #9ca3af; }
  .num { font-variant-numeric: tabular-nums; text-align: right; }
  .arrow { color: var(--adminColor); }
  .strike { color: #9ca3af; text-decoration: line-through; font-size: 11px; }
  .new { color: var(--adminColor); font-weight: 700; }
`;
const StatusBadge = styled.span<{ $type?: 'paid'|'waiting'|'cancelled' }>`
  display: inline-flex; padding: 2px 7px; border-radius: 999px;
  font-size: 10px; font-weight: 600;
  background: ${p => p.$type === 'paid' ? '#dcfce7' : p.$type === 'waiting' ? '#dbeafe' : '#f3f4f6'};
  color:      ${p => p.$type === 'paid' ? '#16a34a' : p.$type === 'waiting' ? '#2563eb' : '#6b7280'};
`;
const ActionGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
const ActionCard = styled.button`
  display: flex; flex-direction: column; align-items: flex-start;
  text-align: left; gap: 6px; padding: 14px 16px;
  border: 1.5px solid #e5e7eb; border-radius: 10px;
  background: #fff; cursor: pointer; transition: all .12s;
  &:hover { border-color: var(--adminColor); background: rgba(111,33,118,.04); }
  .icon {
    width: 32px; height: 32px; border-radius: 8px;
    background: rgba(111,33,118,.1); color: var(--adminColor);
    display: flex; align-items: center; justify-content: center;
  }
  .title { font-size: 13px; font-weight: 700; color: #111827; }
  .desc  { font-size: 11px; color: #6b7280; line-height: 1.4; }
`;
const SegSwitch = styled.div`
  display: flex; gap: 4px; padding: 4px;
  background: #f3f4f6; border-radius: 8px; width: fit-content;
  button {
    border: none; background: transparent; padding: 7px 14px; border-radius: 6px;
    font-size: 12px; font-weight: 600; color: #6b7280; cursor: pointer;
    display: inline-flex; align-items: center; gap: 6px;
    &.active { background: #fff; color: var(--adminColor); box-shadow: 0 1px 3px rgba(0,0,0,.08); }
  }
`;
const BtnCancel = styled.button`
  height: 38px; padding: 0 16px; border-radius: 8px;
  border: 1.5px solid #e5e7eb; background: #fff; color: #374151;
  font-size: 13px; font-weight: 600; cursor: pointer;
  &:hover { background: #f9fafb; }
`;
const BtnPrimary = styled.button`
  height: 38px; padding: 0 18px; border-radius: 8px;
  border: none; background: var(--adminColor); color: #fff;
  font-size: 13px; font-weight: 600; cursor: pointer;
  display: inline-flex; align-items: center; gap: 6px;
  background: #5a1a60;
  &:hover { background: #5a1a60; }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const Empty = styled.div`
  text-align: center; padding: 40px 20px; color: #9ca3af; font-size: 13px;
`;
const Spinner = styled(FiRefreshCw)`
  animation: ${spin} 1s linear infinite;
`;
const ProgressInfo = styled.div`
  font-size: 12px; color: #6b7280; flex: 1;
  strong { color: var(--adminColor); font-weight: 700; }
`;

// =========================================================
// Flow component
// =========================================================
const ReprocessFlow: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { addToast } = useToast();
  const { handleApiErrors } = useAuth();
const {configuration} = useConfiguration();
  const [step, setStep] = useState<Step>('select-client');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  const [search, setSearch] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const [recalcRows, setRecalcRows] = useState<RecalcRow[]>([]);
  const [methodRows, setMethodRows] = useState<MethodRow[]>([]);
  const [targetMethod, setTargetMethod] = useState<PaymentMethod>('credit_card_recurring');

  // ----- load clients (busca todas as invoices e agrupa no front) -----
  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sales-invoice', { params: { limitItems: 10000, where: { project_id : configuration?.url} } });
      const map = new Map<string, Client>();
      (data?.rows || []).forEach((inv: any) => {
        const id   = inv?.client_id || inv?.client_id_response?._id;
        const name = inv?.client_id_response?.name;
        if (!id || !name) return;
        if (!map.has(id)) {
          map.set(id, { _id: id, name, invoice_count: 0, waiting_count: 0 });
        }
        const c = map.get(id)!;
        c.invoice_count = (c.invoice_count || 0) + 1;
        if (inv.payment_status_id === 'waiting') {
          c.waiting_count = (c.waiting_count || 0) + 1;
        }
      });
      const list = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
      setClients(list);
    } catch (err) {
      addToast({ title: 'Erro ao carregar participantes', type: 'error' });
      handleApiErrors(err);
    } finally {
      setLoading(false);
    }
  }, [addToast, handleApiErrors]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const filteredClients = clients.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  // ----- pick client -> load invoices -----
  const pickClient = async (c: Client) => {
    setSelectedClient(c);
    setLoading(true);
    try {
      const { data } = await api.get('/sales-invoice', {
        params: { limitItems: 30, where: { client_id: c._id , project_id : configuration?.url} }
      });
      setInvoices(data?.rows || []);
      setStep('view-invoices');
    } catch (err) {
      addToast({ title: 'Erro ao carregar faturas', type: 'error' });
      handleApiErrors(err);
    } finally {
      setLoading(false);
    }
  };

  // ----- preview recálculo -----
  const previewRecalc = () => {
    const rows: RecalcRow[] = invoices.map(inv => {
      const proposed = computeProposedCharges(inv);
      const current  = {
        juros: inv.juros || 0,
        multa: inv.multa || 0,
        installment_value: inv.installment_value || 0
      };
      const changed  =
        current.juros !== proposed.juros ||
        current.multa !== proposed.multa ||
        current.installment_value !== proposed.installment_value;
      return { invoice: inv, current, proposed, changed };
    });
    setRecalcRows(rows);
    setStep('preview-recalc');
  };

  // ----- preview troca de método -----
  const previewMethod = (newMethod: PaymentMethod) => {
    setTargetMethod(newMethod);
    const rows: MethodRow[] = invoices
      .filter(i => i.payment_status_id === 'waiting'
        && (i.payment_method_id === 'boleto_recurring' || i.payment_method_id === 'credit_card_recurring'))
      .map(i => ({
        invoice: i,
        current_method: i.payment_method_id,
        proposed_method: newMethod,
        changed: i.payment_method_id !== newMethod
      }));
    setMethodRows(rows);
    setStep('preview-method');
  };

  // ----- apply (1 a 1, um PUT por campo alterado) -----
  const applyRecalc = async () => {
    const toUpdate = recalcRows.filter(r => r.changed);
    if (toUpdate.length === 0) { onClose(); return; }
    setApplying(true);
    setProgress({ done: 0, total: toUpdate.length });
    for (let i = 0; i < toUpdate.length; i++) {
      const row = toUpdate[i];
      const id  = row.invoice._id;
      const installmentChanged = row.proposed.installment_value !== row.current.installment_value;
      try {
        // Se vamos alterar o installment_value e ainda não há price salvo,
        // preserva o valor original em price antes de sobrescrever.
        if (installmentChanged && !row.invoice.price) {
          await api.put(`/sales-invoice/${id}/price`, {
            _id: id,
            price: row.invoice.installment_value
          });
        }
        if (row.proposed.juros !== row.current.juros) {
          await api.put(`/sales-invoice/${id}/juros`, { _id: id, juros: row.proposed.juros });
        }
        if (row.proposed.multa !== row.current.multa) {
          await api.put(`/sales-invoice/${id}/multa`, { _id: id, multa: row.proposed.multa });
        }
        if (installmentChanged) {
          await api.put(`/sales-invoice/${id}/installment_value`, {
            _id: id,
            installment_value: row.proposed.installment_value
          });
        }
      } catch { /* segue para o próximo */ }
      setProgress({ done: i + 1, total: toUpdate.length });
    }
    setApplying(false);
    addToast({ title: `${toUpdate.length} fatura(s) recalculada(s)`, type: 'success' });
    if (selectedClient) await pickClient(selectedClient);
  };

  const applyMethodChange = async () => {
    const toUpdate = methodRows.filter(r => r.changed);
    if (toUpdate.length === 0) { onClose(); return; }
    setApplying(true);
    setProgress({ done: 0, total: toUpdate.length });
    for (let i = 0; i < toUpdate.length; i++) {
      const row = toUpdate[i];
      try {
        await api.put(`/sales-invoice/${row.invoice._id}/payment_method_id`, {
          _id: row.invoice._id,
          payment_method_id: row.proposed_method
        });
      } catch { /* segue */ }
      setProgress({ done: i + 1, total: toUpdate.length });
    }
    setApplying(false);
    addToast({ title: `${toUpdate.length} fatura(s) atualizada(s)`, type: 'success' });
    if (selectedClient) await pickClient(selectedClient);
  };

  // =========================================================
  // Renders por step
  // =========================================================
  const renderSelectClient = () => (
    <>
      <Header>
        <HeaderTitle>
          <h3>Reprocessar faturas</h3>
          <p>Selecione o participante para visualizar as faturas dele.</p>
        </HeaderTitle>
        <HeaderBack onClick={onClose}><FiX size={16} /></HeaderBack>
      </Header>
      <Body>
        <SearchBox>
          <FiSearch size={14} />
          <input
            type="text"
            placeholder="Buscar participante por nome…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </SearchBox>
        {loading ? (
          <Empty><Spinner size={20} /> <div style={{ marginTop: 8 }}>Carregando…</div></Empty>
        ) : filteredClients.length === 0 ? (
          <Empty>Nenhum participante encontrado.</Empty>
        ) : (
          <ClientList>
            {filteredClients.map(c => (
              <ClientCard key={c._id} onClick={() => pickClient(c)}>
                <div className="avatar"><FiUser size={14} /></div>
                <div className="info">
                  <div className="name">{c.name}</div>
                  <div className="meta">
                    {c.invoice_count ?? 0} fatura(s)
                    {c.waiting_count != null && ` · ${c.waiting_count} em aberto`}
                  </div>
                </div>
                {c.waiting_count ? (
                  <span className="badge">{c.waiting_count} em aberto</span>
                ) : null}
              </ClientCard>
            ))}
          </ClientList>
        )}
      </Body>
    </>
  );

  const renderViewInvoices = () => {
    const waitingCount = invoices.filter(i => i.payment_status_id === 'waiting').length;
    return (
      <>
        <Header>
          <HeaderBack onClick={() => setStep('select-client')}>
            <FiArrowLeft size={14} /> Voltar
          </HeaderBack>
          <HeaderTitle>
            <h3>{selectedClient?.name}</h3>
            <p>{invoices.length} fatura(s) · {waitingCount} em aberto</p>
          </HeaderTitle>
          <HeaderBack onClick={onClose}><FiX size={16} /></HeaderBack>
        </Header>
        <Body>
          {loading ? (
            <Empty><Spinner size={20} /></Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Nº</th>
                  <th>Vencimento</th>
                  <th>Método</th>
                  <th>Status</th>
                  <th className="num">Valor</th>
                  <th className="num">Juros</th>
                  <th className="num">Multa</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(i => (
                  <tr key={i._id}>
                    <td>#{i.installment_number}</td>
                    <td>{formatDate(i.payment_date)}</td>
                    <td>{methodLabel(i.payment_method_id)}</td>
                    <td><StatusBadge $type={i.payment_status_id as any}>{statusLabel(i.payment_status_id)}</StatusBadge></td>
                    <td className="num">R$ {formatBRL(i.installment_value)}</td>
                    <td className="num">R$ {formatBRL(i.juros)}</td>
                    <td className="num">R$ {formatBRL(i.multa)}</td>
                    <td className="num">R$ {formatBRL(i.total)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <div style={{ marginTop: 18 }}>
            <ActionGrid>
              <ActionCard onClick={previewRecalc}>
                <div className="icon"><FaCalculator size={14} /></div>
                <div className="title">Recalcular atrasos</div>
                <div className="desc">Atualiza juros e multa das faturas em aberto conforme dias de atraso.</div>
              </ActionCard>
              <ActionCard onClick={() => previewMethod('credit_card_recurring')}>
                <div className="icon"><FaCreditCard size={14} /></div>
                <div className="title">Alterar forma de pagamento</div>
                <div className="desc">Troca entre boleto e cartão recorrente nas faturas aguardando.</div>
              </ActionCard>
            </ActionGrid>
          </div>
        </Body>
      </>
    );
  };

  const renderPreviewRecalc = () => {
    const changedCount = recalcRows.filter(r => r.changed).length;
    return (
      <>
        <Header>
          <HeaderBack onClick={() => setStep('view-invoices')} disabled={applying}>
            <FiArrowLeft size={14} /> Voltar
          </HeaderBack>
          <HeaderTitle>
            <h3>Recalcular atrasos · {selectedClient?.name}</h3>
            <p>{changedCount} de {recalcRows.length} fatura(s) sofrerão alteração.</p>
          </HeaderTitle>
        </Header>
        <Body>
          <Table>
            <thead>
              <tr>
                <th>Nº</th>
                <th>Vencimento</th>
                <th>Status</th>
                <th className="num">Juros (atual → novo)</th>
                <th className="num">Multa (atual → novo)</th>
                <th className="num">Valor da fatura (atual → novo)</th>
              </tr>
            </thead>
            <tbody>
              {recalcRows.map(r => (
                <tr key={r.invoice._id} className={r.changed ? 'changed' : 'unchanged'}>
                  <td>#{r.invoice.installment_number}</td>
                  <td>{formatDate(r.invoice.payment_date)}</td>
                  <td><StatusBadge $type={r.invoice.payment_status_id as any}>{statusLabel(r.invoice.payment_status_id)}</StatusBadge></td>
                  <td className="num">
                    {r.changed ? (<>
                      <span className="strike">R$ {formatBRL(r.current.juros)}</span>{' '}
                      <span className="arrow">→</span>{' '}
                      <span className="new">R$ {formatBRL(r.proposed.juros)}</span>
                    </>) : <>R$ {formatBRL(r.current.juros)}</>}
                  </td>
                  <td className="num">
                    {r.changed ? (<>
                      <span className="strike">R$ {formatBRL(r.current.multa)}</span>{' '}
                      <span className="arrow">→</span>{' '}
                      <span className="new">R$ {formatBRL(r.proposed.multa)}</span>
                    </>) : <>R$ {formatBRL(r.current.multa)}</>}
                  </td>
                  <td className="num">
                    {r.changed ? (<>
                      <span className="strike">R$ {formatBRL(r.current.installment_value)}</span>{' '}
                      <span className="arrow">→</span>{' '}
                      <span className="new">R$ {formatBRL(r.proposed.installment_value)}</span>
                    </>) : <>R$ {formatBRL(r.current.installment_value)}</>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Body>
        <Footer>
          {applying ? (
            <ProgressInfo>
              <Spinner size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Atualizando <strong>{progress.done}</strong> de <strong>{progress.total}</strong>…
            </ProgressInfo>
          ) : <div style={{ flex: 1 }} />}
          <BtnCancel onClick={onClose} disabled={applying}>Cancelar</BtnCancel>
          <BtnPrimary onClick={applyRecalc} disabled={applying || changedCount === 0}>
            <FiCheck size={14} />
            Alterar {changedCount > 0 ? `(${changedCount})` : ''}
          </BtnPrimary>
        </Footer>
      </>
    );
  };

  const renderPreviewMethod = () => {
    const changedCount = methodRows.filter(r => r.changed).length;
    return (
      <>
        <Header>
          <HeaderBack onClick={() => setStep('view-invoices')} disabled={applying}>
            <FiArrowLeft size={14} /> Voltar
          </HeaderBack>
          <HeaderTitle>
            <h3>Alterar forma de pagamento · {selectedClient?.name}</h3>
            <p>Aplicado apenas em faturas com status “Aguardando”.</p>
          </HeaderTitle>
        </Header>
        <Body>
          <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Trocar para:</span>
            <SegSwitch>
              <button
                className={targetMethod === 'boleto_recurring' ? 'active' : ''}
                onClick={() => previewMethod('boleto_recurring')}
                disabled={applying}
              >
                <FaBarcode size={11} /> Boleto
              </button>
              <button
                className={targetMethod === 'credit_card_recurring' ? 'active' : ''}
                onClick={() => previewMethod('credit_card_recurring')}
                disabled={applying}
              >
                <FaCreditCard size={11} /> Cartão
              </button>
            </SegSwitch>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
              {changedCount} de {methodRows.length} sofrerão alteração
            </span>
          </div>

          {methodRows.length === 0 ? (
            <Empty>Nenhuma fatura aguardando para alterar.</Empty>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>Nº</th>
                  <th>Vencimento</th>
                  <th className="num">Valor</th>
                  <th>Atual</th>
                  <th></th>
                  <th>Novo</th>
                </tr>
              </thead>
              <tbody>
                {methodRows.map(r => (
                  <tr key={r.invoice._id} className={r.changed ? 'changed' : 'unchanged'}>
                    <td>#{r.invoice.installment_number}</td>
                    <td>{formatDate(r.invoice.payment_date)}</td>
                    <td className="num">R$ {formatBRL(r.invoice.installment_value)}</td>
                    <td>{methodLabel(r.current_method)}</td>
                    <td className="arrow">→</td>
                    <td className={r.changed ? 'new' : ''}>{methodLabel(r.proposed_method)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Body>
        <Footer>
          {applying ? (
            <ProgressInfo>
              <Spinner size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Atualizando <strong>{progress.done}</strong> de <strong>{progress.total}</strong>…
            </ProgressInfo>
          ) : <div style={{ flex: 1 }} />}
          <BtnCancel onClick={onClose} disabled={applying}>Cancelar</BtnCancel>
          <BtnPrimary onClick={applyMethodChange} disabled={applying || changedCount === 0}>
            <FiCheck size={14} />
            Alterar {changedCount > 0 ? `(${changedCount})` : ''}
          </BtnPrimary>
        </Footer>
      </>
    );
  };

  return (
    <Container>
      {step === 'select-client'  && renderSelectClient()}
      {step === 'view-invoices'  && renderViewInvoices()}
      {step === 'preview-recalc' && renderPreviewRecalc()}
      {step === 'preview-method' && renderPreviewMethod()}
    </Container>
  );
};

// =========================================================
// Botão exportado (vai no headModules da SalesInvoice)
// =========================================================
export default function ReprocessInvoicesModule(): JSX.Element {
  const { addModal, removeModal } = useModal();

  const open = () => {
    addModal({
      key: 'reprocess-invoices',
      title: '',
      theme: 'whiteModal',
      content: <ReprocessFlow onClose={() => removeModal('reprocess-invoices')} />
    });
  };

  return (
    <button
      className="searchButton"
      title="Reprocessar faturas por participante"
      type="button"
      onClick={open}
    >
      <FiRefreshCw />
    </button>
  );
}
